import { useEffect, useState } from "react";
import { connect } from "react-redux";
import Cookies from "js-cookie";
import Bugsnag from "@lib/bugsnag";
import NProgress from "nprogress";
import gtm from "@lib/gtm";
import ahoy from "@lib/ahoy";
import isServer from "@utils/is-server";
import { useCatalogNavigation } from "@hooks/use-catalog-navigation-hooks";
import { useCloseDrawer } from "@hooks/use-drawer";
import { useClearFlashes } from "@hooks/use-flash";
import { useRouter } from "next/compat/router";
import {
  useCurrentUser,
  useUserHeaderLastVersion,
  useClosetHeaderLastVersion,
} from "@hooks/use-current-user";
import { useReloadUser } from "@hooks/use-modify-user";
import { useReloadCustomizableCloset } from "@hooks/use-customizable-closet";
import { useCloseCartModal } from "@hooks/use-cart";
import { logUserWithToken } from "@actions/auth-actions";
import { analytics } from "@lib/trackers/analytics";

function AppInit({
  unauthRequired,
  authRequired,
  children,
  // eslint-disable-next-line no-shadow
  logUserWithToken,
}) {
  const [appReady, setReady] = useState(false);

  const router = useRouter();
  const user = useCurrentUser();
  const userHeaderLastVersion = useUserHeaderLastVersion();
  const reloadUser = useReloadUser();
  const closetHeaderLastVersion = useClosetHeaderLastVersion();
  const reloadCustomizableCloset = useReloadCustomizableCloset();
  const catalogNavigation = useCatalogNavigation();
  const closeCartModal = useCloseCartModal();
  const closeDrawer = useCloseDrawer();
  const clearFlashes = useClearFlashes();

  function forceHttps() {
    if (process.env.NEXT_PUBLIC_FORCE_HTTPS !== "true") return;

    const httpTokens = /^http:\/\/(.*)$/.exec(window.location.href);
    if (httpTokens) window.location.replace(`https://${httpTokens[1]}`);
  }

  const redirectTo = (url) => {
    router.push(url);
  };

  const redirectToLoggedPage = (userState) => {
    if (userState === "lead") {
      redirectTo("/je-commence");
    } else {
      redirectTo("/home");
    }
  };

  const redirectToSignIn = (additionalPath) => {
    if (["/", "/home"].includes(additionalPath)) {
      redirectTo("/sign-in");
    } else {
      redirectTo(`/sign-in?redirect=${encodeURIComponent(additionalPath)}`);
    }
  };

  async function checkForUser() {
    const jwt = Cookies.get("jwt-token");
    const loggedUser = await logUserWithToken(jwt);

    if (loggedUser) {
      Bugsnag.user = loggedUser;
      Bugsnag.addOnError((event) => {
        event.addMetadata("user", loggedUser);
      });

      // /sign-in, /sign-up
      if (unauthRequired) redirectToLoggedPage(loggedUser.state);
    } else {
      // /profile, /closets
      if (authRequired) redirectToSignIn(router.asPath);
    }

    setReady(true);
  }

  const handleRouteChangeStart = () => {
    setReady(false);
    NProgress.start();
  };

  const handleRouteChangeComplete = () => {
    clearFlashes();
    NProgress.done();

    closeDrawer();
    closeCartModal();
    setReady(true);
  };

  const handleRouteChangeError = () => NProgress.done();

  useEffect(() => {
    forceHttps();
    checkForUser();
  }, []);

  useEffect(() => {
    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeComplete", handleRouteChangeError);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeError);
    };
  }, [catalogNavigation]);

  useEffect(() => {
    function sendPageViewEvents() {
      if (isServer()) return;

      const url = window.location.pathname + window.location.search;

      // eslint-disable-next-line no-undef
      if (typeof _cio !== "undefined") _cio.page(url);
      analytics.page("pageview", { page: url });
      ahoy("pageView");
      gtm(
        {
          event: "pageview",
          page: window.location.pathname,
        },
        user,
      );
    }

    if (appReady) sendPageViewEvents();
  }, [appReady]);

  useEffect(() => {
    if (userHeaderLastVersion) reloadUser();
  }, [userHeaderLastVersion]);

  useEffect(() => {
    if (closetHeaderLastVersion) reloadCustomizableCloset();
  }, [closetHeaderLastVersion]);

  if (!appReady && (unauthRequired || authRequired)) return null;

  // /sign-in, /sign-up
  if (unauthRequired && user) return null;

  // /profile, /closets
  if (authRequired && !user) return null;

  return children;
}

export default connect(null, {
  logUserWithToken,
})(AppInit);
